// src/pages/SlotCreatePage.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomSelect from "../components/common/CustomSelect";
import { branchApi } from "../Apis/Api";
import Breadcrumb from "../components/Breadcrumb";

const SlotCreatePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    location_id: "",
    slot_count: "",
  });
  const [locations, setLocations] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch all branch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await branchApi.getAllBranchLocations({
          want_pagination: true,
          sort_by: "asc",
          sort_order: "location_name",
        });
        const locationData = response.data || [];
        setLocations(locationData);
      } catch (err) {
        console.error("Error fetching locations:", err);
        toast.error("Failed to fetch locations");
      }
    };
    fetchLocations();
  }, []);

  // react-select ke liye options format
  const locationOptions = locations.map((location) => ({
    value: location.location_id,
    label: `${location.location_name} (${location.branch_name})`,
  }));

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      location_id: selectedOption ? selectedOption.value : "",
    }));
    if (errors.location_id) {
      setErrors((prev) => ({
        ...prev,
        location_id: "",
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.location_id) {
      newErrors.location_id = "Location is required";
    }
    if (!formData.slot_count.trim()) {
      newErrors.slot_count = "Slot count is required";
    } else if (parseInt(formData.slot_count) <= 0) {
      newErrors.slot_count = "Slot count must be greater than 0";
    } else if (!Number.isInteger(parseFloat(formData.slot_count))) {
      newErrors.slot_count = "Slot count must be a whole number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const selectedLocation = locations.find(
          (location) => location.location_id === parseInt(formData.location_id)
        );
        if (!selectedLocation) {
          throw new Error("Selected location not found");
        }

        await branchApi.addParkingSlot({
          branch_id: selectedLocation.branch_id,
          location_id: formData.location_id,
          slot_count: parseInt(formData.slot_count),
        });
        toast.success(`${formData.slot_count} slot(s) created successfully`);
        navigate("/slots");
      } catch (err) {
        toast.error(err.error || "Failed to create slots");
      } finally {
        setLoading(false);
      }
    }
  };

  // react-select custom styles for WowDash theme
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "8px",
      border: errors.location_id ? "1px solid #e53e3e" : "1px solid #e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        border: errors.location_id ? "1px solid #e53e3e" : "1px solid #3b82f6",
      },
      backgroundColor: "#fff",
      padding: "4px",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      zIndex: 1000,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#f3f4f6"
        : "#fff",
      color: state.isSelected ? "#fff" : "#1f2937",
      "&:hover": {
        backgroundColor: "#f3f4f6",
        color: "#1f2937",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#1f2937",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
    }),
  };

  return (
    <>
      <Breadcrumb title="Slot Create" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit}>
            <div className="row g-20">
              <div className="col-md-6 mb-20">
                <label
                  htmlFor="location_id"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Location <span className="text-danger-600">*</span>
                </label>
                <CustomSelect
                  name="location_id"
                  value={formData.location_id}
                  onChange={handleSelectChange}
                  options={locationOptions}
                  placeholder="Select Location"
                  isDisabled={loading}
                  styles={customStyles}
                  isClearable
                  noOptionsMessage={() => "No locations available"}
                  addNewLabel="Add New Location"
                  addNewRoute="/branch-location-create"
                />
                {errors.location_id && (
                  <span className="text-sm text-danger-600 mt-2 d-block">
                    {errors.location_id}
                  </span>
                )}
              </div>
              <div className="col-md-6 mb-20">
                <label
                  htmlFor="slot_count"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Number of Slots <span className="text-danger-600">*</span>
                </label>
                <input
                  type="number"
                  className="form-control radius-8"
                  id="slot_count"
                  name="slot_count"
                  value={formData.slot_count}
                  onChange={handleInputChange}
                  placeholder="e.g., 5"
                  min="1"
                  disabled={loading}
                  style={{
                    border: errors.slot_count ? "1px solid #e53e3e" : "",
                  }}
                />
                {errors.slot_count && (
                  <span className="text-sm text-danger-600 mt-2 d-block">
                    {errors.slot_count}
                  </span>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-center gap-3">
              <Link
                to="/slots"
                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
              >
                Cancel
              </Link>
              <button
                type="submit"
                className="btn btn-primary-600 border border-primary-600 text-md px-56 py-12 radius-8"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div
                      style={{
                        border: "2px solid rgba(255, 255, 255, 0.3)",
                        borderRadius: "50%",
                        borderTopColor: "#fff",
                        width: "16px",
                        height: "16px",
                        animation: "spin 1s ease-in-out infinite",
                        display: "inline-block",
                        marginRight: "8px",
                      }}
                    />
                    Creating...
                  </>
                ) : (
                  "Create Slots"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default SlotCreatePage;
